// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";
import { SchemaForm, Button, View, Text } from "@components";
import type { StepProps } from "@components";
import { removeNullAndUndefinedProps } from "@transformers/object";
import type { Referral, ReferralSupplierTracking } from "@modules/service/reducer/referrals/types";
import {
  CommonReasonInputForm,
  CommonOutcomeInputForm,
  CommonComplianceKpisForm,
} from "@modules/service/components";
import * as serviceSelectors from "@modules/service/selectors";

type OwnProps = $Exact<{
  clientId: number,
  editing: boolean,
  schema: Object,
  data: ReferralSupplierTracking,
  nextButtonLabel: string,
  handleStepChange?: (data: Object) => void,
}>;

type PropsFromState = $Exact<{
  draft: ?Referral,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...StepProps,
  ...PropsFromLocalize,
  ...PropsFromState,
}>;

export class StepSupplierTracking extends React.Component<Props> {
  form: *;
  validator: *;
  errorMessage: *;

  state = {
    formData: null,
  };

  componentDidMount() {
    this.validator = new Ajv().compile(this.schema);
    this.setFormData();
  }

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.editable && this.props.editable) {
      this.setFormData();
    }
  }

  isValid = () => {
    if (this.validator) {
      let isValid = this.validator(removeNullAndUndefinedProps(this.data));
      this.errorMessage = ((this.validator.errors || [])[0] || {}).message || false;

      if (
        this.data.requiredResponse === "ACCEPT_REFUSE" &&
        (this.data.declinedReasons || []).filter(reason => reason.isActive === true).length < 1
      ) {
        isValid = false;
        this.errorMessage = this.props.t("AT_LEAST_ONE_ACTIVE_REASON_MUST_BE_PROVIDED");
      }

      if ((this.data.outcomes || []).length < 1) {
        isValid = false;
        this.errorMessage = this.props.t("NO_OUTCOME_ERROR");
      }

      return isValid;
    }
    return false;
  };
  submitForm = () => {
    if (this.props.handleSubmit) {
      this.props.handleSubmit(this.data, true);
    }
  };
  updateTrackingEnabled = (formData: Object) => {
    const data = { ...formData };
    if (data.requiredResponse === "ACKNOWLEDGE") {
      delete data.declinedReasons;
    }

    this.setState({
      formData: data,
    });
    this.props.onChange(data);
  };

  updateDeclinedReasons = (declinedReasons: Array<Object>) => {
    this.props.onChange({ ...this.data, declinedReasons });
  };

  updateOutcomes = (outcomes: Array<Object>) => {
    this.props.onChange({ ...this.data, outcomes });
  };

  updateKpis = (kpis: Array<Object>) => {
    this.props.onChange({ ...this.data, kpis });
  };

  setFormData = () => {
    this.setState({ formData: this.data });
  };

  get uiSchema() {
    return {
      trackingEnabled: {
        "ui:widget": "hidden",
      },
      requiredResponse: {
        "ui:title": this.props.t("REQUIRED_RESPONSE"),
        "ui:widget": "toggle",
        "ui:options": {
          labels: [
            { label: this.props.t("ACCEPT_REFUSE"), value: "ACCEPT_REFUSE" },
            { label: this.props.t("ACKNOWLEDGE"), value: "ACKNOWLEDGE" },
          ],
        },
      },
      isActive: {
        "ui:widget": "hidden",
      },
      declinedReasons: {
        "ui:widget": "hidden",
      },
      kpis: {
        "ui:widget": "hidden",
      },
      outcomes: {
        "ui:widget": "hidden",
      },
    };
  }
  get schema() {
    return this.props.schema || {};
  }
  get data() {
    const data = {
      ...this.props.data,
      ...((this.props.draft || {}).supplierTracking || {}),
    };

    let res = {
      ...data,
      isActive: true,
      trackingEnabled: true,
      requiredResponse: data.requiredResponse || "ACKNOWLEDGE",
    };
    if (res.requiredResponse === "ACKNOWLEDGE") delete res.declinedReasons;
    return res;
  }
  render() {
    if (!this.props.editable) {
      return <div />;
    }

    const isValid = this.isValid();
    return (
      <View gap={6}>
        {/*Tracking*/}
        <View gap={3} className="gs-container">
          <Text type="h6">{this.props.t("TRACKING")}</Text>
          {this.schema && this.state.formData ? (
            <SchemaForm
              ref={this.form}
              schema={this.schema}
              uiSchema={this.uiSchema}
              formData={this.state.formData}
              externalSubmit
              onChange={this.updateTrackingEnabled}
            />
          ) : null}
        </View>

        {/*Declined Referral*/}
        {this.data.requiredResponse !== "ACKNOWLEDGE" && (
          <View gap={3} className="gs-container">
            <Text type="h6">{this.props.t("DECLINED_REASON")}</Text>
            {this.props.schema && this.props.data ? (
              <CommonReasonInputForm
                data={this.data.declinedReasons}
                schema={this.schema.properties.declinedReasons.items}
                onChange={this.updateDeclinedReasons}
              />
            ) : null}
          </View>
        )}

        {/*Outcome options*/}
        <View gap={3} className="gs-container">
          <Text type="h6">{this.props.t("TREATMENT_OPTIONS")}</Text>
          {this.props.schema && this.props.data ? (
            <CommonOutcomeInputForm
              data={this.data.outcomes}
              schema={this.schema.properties.outcomes.items}
              onChange={this.updateOutcomes}
            />
          ) : null}
        </View>
        {/*Compliance Settings*/}
        <View gap={3} className="gs-container">
          <Text type="h6">{this.props.t("COMPLIANCE_SETTINGS")}</Text>
          {this.props.schema && this.props.data ? (
            <CommonComplianceKpisForm
              data={this.data.kpis}
              schema={this.schema.properties.kpis.items}
              onChange={this.updateKpis}
              kpiTitle={this.props.t("KPI_TARGET_HOURS")}
            />
          ) : null}
        </View>
        {!this.props.isCollapsable && (
          <View direction="row" justifyContent="end" padding="3rem 0 1rem 0">
            <Button onClick={this.submitForm} disabled={!isValid}>
              {this.props.nextButtonLabel}
            </Button>
          </View>
        )}
      </View>
    );
  }
}

const mapStateToProps = (state): PropsFromState => {
  return {
    draft: serviceSelectors.getReferralDraft(state),
  };
};

export default compose(localize(), connect(mapStateToProps, null))(StepSupplierTracking);
